/* eslint-disable react/jsx-no-comment-textnodes */
import { FC, ChangeEvent, useEffect, useState } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useQuery, useMutation } from 'react-query';
import { PutSettingsData, EndUserSettingsFormProps } from './types';
import { useForm, Controller } from 'react-hook-form';
import { Paper, Grid, Button, TextField, Checkbox, Table, TableBody, TableRow } from '@material-ui/core';
import { useStyles } from './styles';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { TableCell } from '@mui/material';
import { getInputProps } from './inputProps';
import { toFormatSum } from 'utils';

export const EndUserSettingsForm: FC<EndUserSettingsFormProps> = observer(({ type, description }) => {
  const classes = useStyles();
  const [passLowerCase, setPassLowerCase] = useState<number>(0);
  const [passUpperCase, setPassUpperCase] = useState<number>(0);
  const [passNumericCase, setPassNumericCase] = useState<number>(0);
  const [passSpecialCase, setPassSpecialCase] = useState<number>(0);
  const [minPass, SetMinPass] = useState<number>(0);
  const [valid, setValid] = useState<boolean>(true);
  const [minName, setMinName] = useState<number>(0);
  const [maxName, setMaxName] = useState<number>(0);
  const [errorArray, setErrorArray] = useState<any[]>([]);
  const [focused, setIsFocused] = useState<any>({ type: '', isfocused: false });
  const {
    endUserSettingsFormStore: { settingsByTypeView, getSettings, getShift, updateSettings },
    notificationStore: { openNotification },
  } = useStore();
  const getSettingsReq = useQuery('getSettingsReq', () => getSettings(), {
    onError: () => openNotification('error', `Can't get settings data`),
  });
  const updateSettingsReq = useMutation((data: PutSettingsData) => updateSettings(data), {
    mutationKey: 'updatePoliciesReq',
    onSuccess: () => openNotification('success', 'Saved!!'),
    onError: () => openNotification('error', `Can't update policies`),
  });
  useEffect(() => {
    settingsByTypeView(type).forEach((group: any, i: any) =>
      group.settings.forEach((setting: any, j: any) => {
        if (setting.key === 'minimumNumberOfLowercaseCharacters') {
          return setPassLowerCase(+setting.value);
        } else if (setting.key === 'minimumNumberOfUppercaseCharacters') {
          return setPassUpperCase(+setting.value);
        } else if (setting.key === 'minimumNumberOfNumericCharacters') {
          return setPassNumericCase(+setting.value);
        } else if (setting.key === 'minimumNumberOfSpecialCharacters') {
          return setPassSpecialCase(+setting.value);
        } else if (setting.key === 'minimumLengthPassword') {
          return SetMinPass(+setting.value);
        } else if (setting.key === 'maximumLength') {
          return setMaxName(+setting.value);
        } else if (setting.key === 'minimumLengthName') {
          return setMinName(+setting.value);
        } else {
          return 0;
        }
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSettingsReq.isFetching]);
  useEffect(() => {
    const handlePassword = () => {
      const values = passLowerCase + passUpperCase + passNumericCase + passSpecialCase;
      if (values > minPass) {
        setValid(false);
      } else {
        setValid(true);
      }
    };
    handlePassword();
    const handleName = () => {
      if (minName > maxName) {
        setValid(false);
      } else {
        setValid(true);
      }
    };
    handleName();
    handlePassword();
  }, [passLowerCase, passUpperCase, passNumericCase, passSpecialCase, minPass, minName, maxName]);
  useEffect(() => {
    const handleName = () => {
      if (minName > maxName) {
        setValid(false);
      } else {
        setValid(true);
      }
    };
    handleName();
  }, [minName, maxName]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, setting: any, field: any) => {
    const inputValue = e.target.value;
    switch (setting.key) {
      case 'minimumNumberOfLowercaseCharacters':
        setPassLowerCase(+inputValue);
        break;
      case 'minimumNumberOfUppercaseCharacters':
        setPassUpperCase(+inputValue);
        break;
      case 'minimumNumberOfNumericCharacters':
        setPassNumericCase(+inputValue);
        break;
      case 'minimumNumberOfSpecialCharacters':
        setPassSpecialCase(+inputValue);
        break;
      case 'minimumLengthPassword':
        SetMinPass((prev) => (prev = +inputValue));
        break;
      case 'maximumLength':
        setMaxName(+inputValue);
        break;
      case 'minimumLengthName':
        setMinName((prev) => (prev = +inputValue));
        break;
      default:
        field.onChange(e);
        break;
    }
    const isValid = getInputProps(setting).isValidInput(+inputValue);
    if (!isValid) {
      if (!errorArray.some((item) => item.key === setting.key)) {
        setErrorArray((prev: any) => [
          ...prev,
          { key: setting.key, valueType: setting.valueType, min: setting.min, max: setting.max },
        ]);
      }
    } else {
      setErrorArray((prev) => prev.filter((item) => item.key !== setting.key));
    }
  };
  useAppPreloader(getSettingsReq.isFetching || updateSettingsReq.isLoading);
  const { control, handleSubmit } = useForm<PutSettingsData>();
  const onSubmit = (data: PutSettingsData) => {
    updateSettingsReq.mutate(data);
  };
  useEffect(() => {
    type === 'user-name'
      ? (document.title = 'EasyIdentity: Settings: Username Policy')
      : (document.title = 'EasyIdentity: Settings: Password Policy');
  }, [type]);
  return !getSettingsReq.isFetching ? (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.box}>
      <Grid container justify='space-between'>
        {description && type !== 'general' ? (
          <h2 className={classes.textBox}>{description}</h2>
        ) : (
          <h2 className={classes.textBox}>
            End-User portal link is{' '}
            <Link to={'/'} target='_blank' className={classes.link}>
              {window.location.protocol}
              {'//'}
              {window.location.host}
            </Link>
          </h2>
        )}
        <Paper className={classes.paper}>
          {settingsByTypeView(type).map((group: any, i: any) => (
            <TableRow>
              <Grid className={classes.titlecontent}>
                <p>{group.name}</p>
              </Grid>
              <Grid container className={classes.row}>
                {group.settings.map((setting: any, j: any) => (
                  <>
                    <Controller
                      name={`data.${getShift(type, i) + j}.key` as const}
                      defaultValue={setting.key}
                      control={control}
                      render={() => <></>}
                    />
                    <Controller
                      name={`data.${getShift(type, i) + j}.value` as const}
                      defaultValue={setting.value}
                      control={control}
                      rules={{ pattern: setting.valueType === 'numeric' ? /^[0-9]+$/ : undefined }}
                      render={({ field, fieldState: { invalid, error } }) => (
                        <Grid container key={setting.key}>
                          {setting.name ? (
                            <Table className={classes.border}>
                              <TableBody>
                                <TableCell className={`${classes.border1} ${classes.cellTitle}`}>
                                  {setting.name}
                                </TableCell>
                                <TableCell>
                                  {setting.type === 'checkbox' ? (
                                    <>
                                      <Checkbox
                                        autoFocus={i === 1}
                                        checked={field.value !== '0' && field.value !== ''}
                                        onChange={(event: ChangeEvent, checked: boolean) =>
                                          field.onChange(`${+checked}`)
                                        }
                                      />
                                      <Grid className={classes.title} style={{ width: 'auto' }}>
                                        <p>{setting.description}</p>
                                      </Grid>
                                    </>
                                  ) : (setting.type === 'textFields' &&
                                      setting.valueType === 'numeric' &&
                                      setting.key === 'minimumNumberOfLowercaseCharacters') ||
                                    (setting.valueType === 'numeric' &&
                                      setting.key === 'minimumNumberOfUppercaseCharacters') ||
                                    (setting.valueType === 'numeric' &&
                                      setting.key === 'minimumNumberOfNumericCharacters') ||
                                    (setting.valueType === 'numeric' &&
                                      setting.key === 'minimumNumberOfSpecialCharacters') ||
                                    (setting.valueType === 'numeric' && setting.key === 'minimumLengthPassword') ? (
                                    <>
                                      <TextField
                                        {...field}
                                        className={classes.input}
                                        type='text'
                                        error={
                                          invalid ||
                                          (valid === false && setting.key !== 'minimumLengthPassword' && focused) ||
                                          !getInputProps(setting).isValidInput(field.value)
                                        }
                                        value={
                                          setting.valueType === 'numeric'
                                            ? toFormatSum(field.value).replace(/^0+/, '')
                                            : field.value
                                        }
                                        inputProps={getInputProps(setting).inputProps}
                                        onFocus={() => setIsFocused({ type: setting.key, isfocused: true })}
                                        onBlur={() => setIsFocused({ type: setting.key, isfocused: false })}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                          field.onChange(e);
                                          handleInputChange(e, setting, field);
                                        }}
                                        helperText={
                                          valid === false && setting.key !== 'minimumLengthPassword' && focused
                                            ? 'Password must be equal to or less than the minimum password length.'
                                            : invalid
                                            ? error?.type === 'pattern'
                                              ? 'This field must be a number'
                                              : 'Required field'
                                            : !getInputProps(setting).isValidInput(field.value)
                                            ? getInputProps(setting).inputProps.title
                                            : ''
                                        }
                                      />
                                      <Grid className={classes.desc}>
                                        <p>{setting.description}</p>
                                      </Grid>
                                    </>
                                  ) : (setting.type === 'textFields' &&
                                      setting.valueType === 'numeric' &&
                                      setting.key === 'maximumLength') ||
                                    (setting.valueType === 'numeric' && setting.key === 'minimumLengthName') ? (
                                    <>
                                      <TextField
                                        {...field}
                                        className={classes.input}
                                        type='text'
                                        error={
                                          invalid ||
                                          (valid === false && setting.key !== 'maximumLength' && focused) ||
                                          !getInputProps(setting).isValidInput(field.value)
                                        }
                                        value={
                                          setting.valueType === 'numeric'
                                            ? toFormatSum(field.value).replace(/^0+/, '')
                                            : field.value
                                        }
                                        inputProps={getInputProps(setting).inputProps}
                                        onFocus={() => setIsFocused({ type: setting.key, isfocused: true })}
                                        onBlur={() => setIsFocused({ type: setting.key, isfocused: false })}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                          field.onChange(e);
                                          handleInputChange(e, setting, field);
                                        }}
                                        helperText={
                                          valid === false && setting.key !== 'maximumLength' && focused
                                            ? 'Minimum Length must be equal to or less than the maximum length.'
                                            : invalid
                                            ? error?.type === 'pattern'
                                              ? 'This field must be a number'
                                              : 'Required field'
                                            : !getInputProps(setting).isValidInput(field.value)
                                            ? getInputProps(setting).inputProps.title
                                            : ''
                                        }
                                      />
                                      <Grid className={classes.desc}>
                                        <p>{setting.description}</p>
                                      </Grid>
                                    </>
                                  ) : (
                                    <>
                                      <TextField
                                        {...field}
                                        className={classes.input}
                                        type={setting.valueType === 'numeric' ? 'text' : 'textArea'}
                                        autoFocus={i === 0}
                                        multiline={setting.type === 'textArea'}
                                        rows={setting.type === 'textArea' ? 6 : 1}
                                        error={invalid || !getInputProps(setting).isValidInput(field.value)}
                                        inputProps={getInputProps(setting).inputProps}
                                        value={
                                          setting.valueType === 'numeric'
                                            ? toFormatSum(field.value).replace(/^0+/, '')
                                            : field.value
                                        }
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                          field.onChange(e);
                                          handleInputChange(e, setting, field);
                                        }}
                                        helperText={
                                          invalid
                                            ? error?.type === 'pattern'
                                              ? 'This field must be a number'
                                              : 'Required field'
                                            : !getInputProps(setting).isValidInput(field.value)
                                            ? getInputProps(setting).inputProps.title
                                            : ''
                                        }
                                      />
                                      <Grid className={classes.desc}>
                                        <p>{setting.description}</p>
                                      </Grid>
                                    </>
                                  )}
                                </TableCell>
                              </TableBody>
                            </Table>
                          ) : null}
                        </Grid>
                      )}
                    />
                  </>
                ))}
              </Grid>
            </TableRow>
          ))}
        </Paper>
      </Grid>
      <div className={classes.btnBox}>
        <Button
          type='submit'
          disabled={updateSettingsReq.isLoading || valid === false || errorArray.length > 0}
          variant='contained'
          color='secondary'
          className={classes.btnWide}>
          Save Changes
        </Button>
      </div>
    </form>
  ) : null;
});
